import * as React from 'react';
import { InputBase } from '@mui/material';
import { Box, styled } from '@mui/system';
import { FC, Fragment } from 'react';
import { grey, blue, red } from '@mui/material/colors';
import { Typography } from '@mui/material';

interface OTPTextInputProps {
  separator?: React.ReactNode;
  length: number;
  value: string;
  onChange: React.Dispatch<React.SetStateAction<string>>;
  label?: string;
  error?: boolean;
  helperText?: string;
}

const OTPTextInput: FC<OTPTextInputProps> = ({
  separator = <span>-</span>,
  length,
  value,
  onChange,
  label,
  error = false,
  helperText,
}) => {
  const inputRefs = React.useRef<HTMLInputElement[]>([]);

  const focusInput = (index: number) => inputRefs.current[index]?.focus();

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
    index: number
  ) => {
    const key = event.key;
    if (key === 'ArrowLeft' && index > 0) {
      event.preventDefault();
      focusInput(index - 1);
    } else if (key === 'ArrowRight' && index < length - 1) {
      event.preventDefault();
      focusInput(index + 1);
    } else if (key === 'Backspace') {
      event.preventDefault();
      onChange((prev) => {
        const chars = prev.split('');
        chars[index] = '';
        return chars.join('');
      });
      if (index > 0) {
        focusInput(index - 1);
      }
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const val = e.target.value.replace(/\D/g, '').slice(-1);
    if (!val) {
      return;
    }

    const updated = value.split('');
    updated[index] = val;
    onChange(updated.join(''));

    if (index < length - 1) {
      focusInput(index + 1);
    }
  };

  const handlePaste = (
    event: React.ClipboardEvent<HTMLInputElement>,
    index: number
  ) => {
    event.preventDefault();
    const pasted = event.clipboardData
      .getData('text/plain')
      .replace(/\D/g, '')
      .slice(0, length);
    const updated = value.split('');
    for (let i = 0; i < length - index && i < pasted.length; i++) {
      updated[index + i] = pasted[i];
    }
    onChange(updated.join(''));
  };

  return (
    <Box>
      {label && (
        <Typography
          variant="body2"
          sx={{
            fontWeight: 500,
            marginBottom: 1,
          }}
        >
          {label}
        </Typography>
      )}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        {Array.from({ length }).map((_, i) => (
          <Fragment key={i}>
            <InputBase
              slots={{ input: StyledInput }}
              slotProps={{
                input: {
                  // TODO this was used by mui base from mui/lab
                  // ref: (el: HTMLInputElement) => {
                  //   inputRefs.current[i] = el!;
                  // },
                  value: value[i] ?? '',
                  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) =>
                    handleKeyDown(e, i),
                  onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                    handleChange(e, i),
                  onPaste: (e: React.ClipboardEvent<HTMLInputElement>) =>
                    handlePaste(e, i),
                  inputMode: 'numeric',
                  maxLength: 1,
                  'aria-label': `OTP Digit ${i + 1}`,
                },
              }}
            />
            {i < length - 1 && separator}
          </Fragment>
        ))}
      </Box>
      {helperText && (
        <Typography
          variant="caption"
          sx={{
            color: error ? red[700] : grey[600],
            mt: 0.5,
            display: 'block',
          }}
        >
          {helperText}
        </Typography>
      )}
    </Box>
  );
};

const StyledInput = styled('input')(() => ({
  width: 40,
  height: 48,
  fontSize: '1.2rem',
  fontWeight: 600,
  textAlign: 'center',
  borderRadius: 8,
  color: grey[900],
  background: '#fff',
  border: `1px solid ${grey[300]}`,
  boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
  '&:hover': {
    borderColor: blue[400],
  },
  '&:focus': {
    borderColor: blue[400],
    outline: 'none',
    boxShadow: `0 0 0 3px ${blue[100]}`,
  },
}));

export default OTPTextInput;
